import { DatePicker, Spin } from 'antd';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import i18n from '../../../i18n/config';
import { UserMyAssignmentsType, userStatisticsService } from '../../../services/user.statistics.service';
import { COLORS, DASHBOARD_CHART_COLORS } from '../../../shared';
import { HasChart } from '../../atoms';

interface UserMyAssignmentsProps {
  activeTab?: string;
}

const UserMyAssignments: React.FC<UserMyAssignmentsProps> = ({ activeTab = 'actions' }) => {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [data, setData] = useState<UserMyAssignmentsType | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    userStatisticsService
      .getMyAssignments(year)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [year]);

  const monthLabels = data ? data.months.map((m) => moment(m, 'YYYY-MM').format('MMM')) : [];

  const getTabLabel = () => {
    switch (activeTab) {
      case 'inspections':
        return i18n.t('inspection.inspections', 'Inspections');
      case 'maintenances':
        return i18n.t('maintenance.maintenances', 'Maintenances');
      case 'riskAssessments':
        return i18n.t('riskAssessment.riskAssessments', 'Risk Assessments');
      default:
        return i18n.t('shared.actions', 'Actions');
    }
  };

  const getTabData = (): number[] => {
    if (!data) return [];
    switch (activeTab) {
      case 'inspections':
        return data.inspections;
      case 'maintenances':
        return data.maintenances;
      case 'riskAssessments':
        return data.riskAssessments;
      default:
        return data.actions;
    }
  };

  const series = data ? [{ name: getTabLabel(), data: getTabData() }] : [];

  const options: any = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'Montserrat',
    },
    colors: DASHBOARD_CHART_COLORS,
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '70%',
        borderRadius: 2,
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: monthLabels,
      labels: {
        style: { fontSize: '11px', colors: COLORS.GRAY_ACCENT_DARK },
      },
    },
    yaxis: {
      labels: {
        style: { fontSize: '11px', colors: COLORS.GRAY_ACCENT_DARK },
        formatter: (v: number) => Math.round(v).toString(),
      },
    },
    legend: {
      position: 'top',
      fontSize: '12px',
      fontFamily: 'Montserrat',
    },
    tooltip: { shared: true, intersect: false },
    grid: { borderColor: COLORS.GRAY_ACCENT_LIGHT },
  };

  return (
    <div
      style={{
        backgroundColor: COLORS.WHITE,
        border: `1px solid ${COLORS.GRAY_ACCENT_LIGHT}`,
        padding: '10px',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
      className="shadow-sm"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
          flexShrink: 0,
        }}
      >
        <h3 className="ant-descriptions-title" style={{ marginLeft: 9, marginTop: 3 }}>
          {`${getTabLabel().toUpperCase()} - ${i18n.t('user.statistics.myAssignments', 'MY ASSIGNMENTS')}`}
        </h3>
        <DatePicker
          picker="year"
          value={moment().year(year)}
          onChange={(m: Moment | null) => {
            if (m) setYear(m.year());
          }}
          allowClear={false}
          style={{ width: 100 }}
        />
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
            <Spin />
          </div>
        ) : (
          <HasChart options={options} series={series} type="bar" height={'100%'} />
        )}
      </div>
    </div>
  );
};

export default UserMyAssignments;
